import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { Calendar, momentLocalizer, Event, View } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CustomToolbar from './CustomToolbar';
import { FaUser, FaEnvelope, FaPhone, FaBed, FaCalendarAlt, FaRupeeSign, FaUsers, FaEdit, FaTrash, FaEye, FaTimes, FaCheck, FaClock, FaMapMarkerAlt } from 'react-icons/fa';

const localizer = momentLocalizer(moment);

interface Booking {
    id: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    checkInDate: string;
    checkOutDate: string;
    roomType: string;
    guestCount: number;
    totalCost: number;
    specialRequests?: string;
    paymentStatus: string;
    createdAt: string;
}

interface CalendarEvent extends Event {
    id: string;
    title: string;
    start: Date;
    end: Date;
    resource: Booking;
}

const ManageAppointments: React.FC = () => {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [view, setView] = useState<View>('month');
    const [date, setDate] = useState<Date>(new Date());
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');
    const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'confirmed' | 'cancelled'>('all');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchBookings = async () => {
            const querySnapshot = await getDocs(collection(db, 'bookings'));
            const bookingsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Booking[];
            setBookings(bookingsData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        };

        fetchBookings();
    }, []);

    useEffect(() => {
        const eventsData = bookings.map(booking => {
            const start = new Date(booking.checkInDate);
            const end = new Date(booking.checkOutDate);
            return {
                id: booking.id,
                title: `${booking.customerName} - ${booking.roomType}`,
                start,
                end,
                resource: booking
            };
        });
        setEvents(eventsData);
    }, [bookings]);

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
            try {
                await deleteDoc(doc(db, 'bookings', id));
                setBookings(bookings.filter(booking => booking.id !== id));
                setShowModal(false);
            } catch (error) {
                console.error('Error deleting booking:', error);
                alert('Failed to delete booking. Please try again.');
            }
        }
    };

    const handleStatusUpdate = async (id: string, newStatus: string) => {
        try {
            await updateDoc(doc(db, 'bookings', id), {
                paymentStatus: newStatus
            });
            setBookings(bookings.map(booking => 
                booking.id === id ? { ...booking, paymentStatus: newStatus } : booking
            ));
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status. Please try again.');
        }
    };

    const filteredBookings = bookings.filter(booking => {
        const matchesSearch = booking.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            booking.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            booking.roomType.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' || booking.paymentStatus === filterStatus;
        return matchesSearch && matchesFilter;
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'confirmed': return 'bg-green-100 text-green-800 border-green-200';
            case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'confirmed': return <FaCheck className="w-3 h-3" />;
            case 'pending': return <FaClock className="w-3 h-3" />;
            case 'cancelled': return <FaTimes className="w-3 h-3" />;
            default: return <FaClock className="w-3 h-3" />;
        }
    };

    const getDaysDiff = (checkIn: string, checkOut: string) => {
        const start = new Date(checkIn);
        const end = new Date(checkOut);
        const timeDiff = end.getTime() - start.getTime();
        return Math.ceil(timeDiff / (1000 * 3600 * 24));
    };

    const label = () => {
        if (view === 'day') {
            return date.toLocaleDateString('default', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } else if (view === 'week') {
            const startOfWeek = new Date(date);
            startOfWeek.setDate(date.getDate() - date.getDay());
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            const startLabel = startOfWeek.toLocaleDateString('default', {
                month: 'short',
                day: 'numeric'
            });
            const endLabel = endOfWeek.toLocaleDateString('default', {
                month: 'short',
                day: 'numeric'
            });
            return `${startLabel} - ${endLabel}`;
        } else {
            const month = date.toLocaleString('default', { month: 'long' });
            const year = date.getFullYear();
            return `${month} ${year}`;
        }
    };

    const eventStyleGetter = (event: CalendarEvent) => {
        const status = event.resource.paymentStatus;
        let backgroundColor = '#3174ad';
        
        switch (status) {
            case 'confirmed':
                backgroundColor = '#10b981';
                break;
            case 'pending':
                backgroundColor = '#f59e0b';
                break;
            case 'cancelled':
                backgroundColor = '#ef4444';
                break;
        }

        return {
            style: {
                backgroundColor,
                borderRadius: '5px',
                opacity: 0.8,
                color: 'white',
                border: '0px',
                display: 'block'
            }
        };
    };

    const handleEventClick = (event: CalendarEvent) => {
        setSelectedBooking(event.resource);
        setShowModal(true);
    };

    const handleNavigate = (action: 'PREV' | 'NEXT' | 'TODAY') => {
        const newDate = new Date(date);
        if (action === 'PREV') {
            if (view === 'month' || view === 'agenda') {
                newDate.setMonth(newDate.getMonth() - 1);
            } else if (view === 'week') {
                newDate.setDate(newDate.getDate() - 7);
            } else if (view === 'day') {
                newDate.setDate(newDate.getDate() - 1);
            }
        } else if (action === 'NEXT') {
            if (view === 'month' || view === 'agenda') {
                newDate.setMonth(newDate.getMonth() + 1);
            } else if (view === 'week') {
                newDate.setDate(newDate.getDate() + 7);
            } else if (view === 'day') {
                newDate.setDate(newDate.getDate() + 1);
            }
        } else if (action === 'TODAY') {
            newDate.setFullYear(new Date().getFullYear());
            newDate.setMonth(new Date().getMonth());
            newDate.setDate(new Date().getDate());
        }
        setDate(newDate);
    };

    return (
        <div className="p-4 md:p-6 bg-white rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 space-y-4 lg:space-y-0">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">Booking Management</h2>
                    <p className="text-gray-600 mt-1">Manage all resort bookings and reservations</p>
                </div>
                
                {/* View Toggle */}
                <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                    <button
                        onClick={() => setViewMode('calendar')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            viewMode === 'calendar' 
                                ? 'bg-primary text-white shadow-sm' 
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        Calendar View
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            viewMode === 'list' 
                                ? 'bg-primary text-white shadow-sm' 
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        List View
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-100 text-sm">Total Bookings</p>
                            <p className="text-2xl font-bold">{bookings.length}</p>
                        </div>
                        <FaBed className="text-3xl text-blue-200" />
                    </div>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-100 text-sm">Confirmed</p>
                            <p className="text-2xl font-bold">{bookings.filter(b => b.paymentStatus === 'confirmed').length}</p>
                        </div>
                        <FaCheck className="text-3xl text-green-200" />
                    </div>
                </div>
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-yellow-100 text-sm">Pending</p>
                            <p className="text-2xl font-bold">{bookings.filter(b => b.paymentStatus === 'pending').length}</p>
                        </div>
                        <FaClock className="text-3xl text-yellow-200" />
                    </div>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-100 text-sm">Total Revenue</p>
                            <p className="text-2xl font-bold">₹{bookings.reduce((sum, b) => sum + b.totalCost, 0).toLocaleString()}</p>
                        </div>
                        <FaRupeeSign className="text-3xl text-purple-200" />
                    </div>
                </div>
            </div>

            {viewMode === 'list' && (
                <>
                    {/* Filters */}
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                        <div className="flex-1">
                            <input
                                type="text"
                                placeholder="Search by name, email, or room type..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                            />
                        </div>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value as any)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                        >
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    {/* Bookings List */}
                    <div className="space-y-4">
                        {filteredBookings.map((booking) => (
                            <div key={booking.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                                <div className="flex flex-col lg:flex-row lg:items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <h3 className="text-lg font-semibold text-gray-800">{booking.customerName}</h3>
                                            <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.paymentStatus)}`}>
                                                {getStatusIcon(booking.paymentStatus)}
                                                <span className="capitalize">{booking.paymentStatus}</span>
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                                            <div className="flex items-center space-x-2">
                                                <FaEnvelope className="text-gray-400" />
                                                <span>{booking.customerEmail}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <FaPhone className="text-gray-400" />
                                                <span>{booking.customerPhone}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <FaBed className="text-gray-400" />
                                                <span>{booking.roomType}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <FaCalendarAlt className="text-gray-400" />
                                                <span>{new Date(booking.checkInDate).toLocaleDateString()} - {new Date(booking.checkOutDate).toLocaleDateString()}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <FaUsers className="text-gray-400" />
                                                <span>{booking.guestCount} Guest{booking.guestCount > 1 ? 's' : ''}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <FaRupeeSign className="text-gray-400" />
                                                <span className="font-semibold">₹{booking.totalCost.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2 mt-4 lg:mt-0">
                                        <button
                                            onClick={() => {
                                                setSelectedBooking(booking);
                                                setShowModal(true);
                                            }}
                                            className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                                        >
                                            <FaEye className="w-3 h-3" />
                                            <span>View</span>
                                        </button>
                                        <button
                                            onClick={() => handleDelete(booking.id)}
                                            className="flex items-center space-x-1 px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm"
                                        >
                                            <FaTrash className="w-3 h-3" />
                                            <span>Delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {filteredBookings.length === 0 && (
                            <div className="text-center py-12">
                                <FaBed className="mx-auto text-6xl text-gray-300 mb-4" />
                                <h3 className="text-xl font-semibold text-gray-600 mb-2">No bookings found</h3>
                                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {viewMode === 'calendar' && (
                <>
                    <CustomToolbar
                        label={label()}
                        onNavigate={handleNavigate}
                        onView={setView}
                    />
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: 500 }}
                        views={{ month: true, week: true, day: true, agenda: true }}
                        eventPropGetter={eventStyleGetter}
                        onSelectEvent={handleEventClick}
                        popup={true}
                        selectable={true}
                        view={view}
                        onView={setView}
                        date={date}
                        onNavigate={setDate}
                        toolbar={false}
                    />
                </>
            )}

            {/* Booking Details Modal - Simplified and Mobile-Friendly */}
            {showModal && selectedBooking && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowModal(false);
                        }
                    }}
                >
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        {/* Simple Header */}
                        <div className="bg-primary text-white p-4 rounded-t-lg flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold">Booking Details</h3>
                                <p className="text-sm opacity-90">ID: {selectedBooking.id.slice(-6).toUpperCase()}</p>
                            </div>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-white hover:text-gray-200 p-1"
                            >
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Simple Content */}
                        <div className="p-4 space-y-4">
                            {/* Customer Info */}
                            <div className="bg-gray-50 rounded-lg p-3">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FaUser className="mr-2 text-primary text-sm" />
                                    Customer
                                </h4>
                                <div className="space-y-1 text-sm">
                                    <p><span className="font-medium">Name:</span> {selectedBooking.customerName}</p>
                                    <p><span className="font-medium">Email:</span> {selectedBooking.customerEmail}</p>
                                    <p><span className="font-medium">Phone:</span> {selectedBooking.customerPhone}</p>
                                    <p><span className="font-medium">Guests:</span> {selectedBooking.guestCount}</p>
                                </div>
                            </div>

                            {/* Booking Info */}
                            <div className="bg-gray-50 rounded-lg p-3">
                                <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FaBed className="mr-2 text-primary text-sm" />
                                    Booking
                                </h4>
                                <div className="space-y-1 text-sm">
                                    <p><span className="font-medium">Room:</span> {selectedBooking.roomType}</p>
                                    <p><span className="font-medium">Check-in:</span> {new Date(selectedBooking.checkInDate).toLocaleDateString()}</p>
                                    <p><span className="font-medium">Check-out:</span> {new Date(selectedBooking.checkOutDate).toLocaleDateString()}</p>
                                    <p><span className="font-medium">Duration:</span> {getDaysDiff(selectedBooking.checkInDate, selectedBooking.checkOutDate)} night{getDaysDiff(selectedBooking.checkInDate, selectedBooking.checkOutDate) > 1 ? 's' : ''}</p>
                                    <p><span className="font-medium">Total:</span> <span className="text-lg font-bold text-primary">₹{selectedBooking.totalCost.toLocaleString()}</span></p>
                                </div>
                            </div>

                            {/* Special Requests */}
                            {selectedBooking.specialRequests && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <h4 className="font-semibold text-gray-800 mb-2">Special Requests</h4>
                                    <p className="text-sm text-gray-700">{selectedBooking.specialRequests}</p>
                                </div>
                            )}

                            {/* Status Management */}
                            <div className="bg-gray-50 rounded-lg p-3">
                                <h4 className="font-semibold text-gray-800 mb-3">Update Status</h4>
                                <div className="grid grid-cols-1 gap-2">
                                    <button
                                        onClick={() => {
                                            handleStatusUpdate(selectedBooking.id, 'pending');
                                            setSelectedBooking({...selectedBooking, paymentStatus: 'pending'});
                                        }}
                                        className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors ${
                                            selectedBooking.paymentStatus === 'pending'
                                                ? 'bg-yellow-500 text-white'
                                                : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                        }`}
                                    >
                                        Pending
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleStatusUpdate(selectedBooking.id, 'confirmed');
                                            setSelectedBooking({...selectedBooking, paymentStatus: 'confirmed'});
                                        }}
                                        className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors ${
                                            selectedBooking.paymentStatus === 'confirmed'
                                                ? 'bg-green-500 text-white'
                                                : 'bg-green-100 text-green-800 hover:bg-green-200'
                                        }`}
                                    >
                                        Confirmed
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleStatusUpdate(selectedBooking.id, 'cancelled');
                                            setSelectedBooking({...selectedBooking, paymentStatus: 'cancelled'});
                                        }}
                                        className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors ${
                                            selectedBooking.paymentStatus === 'cancelled'
                                                ? 'bg-red-500 text-white'
                                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                                        }`}
                                    >
                                        Cancelled
                                    </button>
                                </div>
                            </div>

                            {/* Booking Date */}
                            <div className="text-center">
                                <p className="text-xs text-gray-500">
                                    Booked: {new Date(selectedBooking.createdAt).toLocaleDateString()}
                                </p>
                            </div>
                        </div>

                        {/* Simple Footer */}
                        <div className="bg-gray-50 px-4 py-3 rounded-b-lg flex gap-2">
                            <button
                                onClick={() => {
                                    if (window.confirm('Delete this booking?')) {
                                        handleDelete(selectedBooking.id);
                                    }
                                }}
                                className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm"
                            >
                                <FaTrash className="w-3 h-3" />
                                <span>Delete</span>
                            </button>
                            <button
                                onClick={() => setShowModal(false)}
                                className="flex-1 px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors text-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageAppointments;